import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import {
  UserCheck, UserX, Users, Wifi, WifiOff, Clock, Save, Edit3,
  CheckCircle, XCircle, AlertCircle, User, ClipboardCheck,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import {
  PageHeader, AppButton, AppCard, AppCardContent, AppCardHeader, AppCardTitle,
  StatsCard, StatsGrid, SearchInput, EmptyState, Separator,
} from "../ui";
import CommonToaster from "../Common/CommonToaster";
import "./attendance.scss";

/**
 * AttendanceSessionDetail — View and edit attendance for a single session.
 * Route: /attendance/session/:sessionId
 */
const AttendanceSessionDetail = ({ basePath = "/attendance" }) => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [session, setSession] = useState(null);
  const [allStudents, setAllStudents] = useState([]);
  const [absentStudents, setAbsentStudents] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [editedParticipants, setEditedParticipants] = useState([]);
  const [offlineEnabled, setOfflineEnabled] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [sessionRes, offlineRes] = await Promise.all([
        apiClient.get(`/attendance/session/${sessionId}`),
        apiClient.get("/attendance/check-offline-enabled"),
      ]);
      setSession(sessionRes.data.session);
      setAllStudents(sessionRes.data.allStudents || []);
      setAbsentStudents(sessionRes.data.absentStudents || []);
      setOfflineEnabled(offlineRes.data.offlineAttendanceEnabled);
      setEditedParticipants(
        (sessionRes.data.session?.participants || []).map((p) => ({
          studentId: p.student?._id || p.student,
          name: p.student?.name || p.name,
          email: p.student?.email || "",
          status: p.attendanceStatus,
          source: p.source || "online",
          activeMinutes: p.activeMinutes,
          attendancePct: p.attendancePct,
          note: p.manualNote || "",
        }))
      );
    } catch {
      setToast({ show: true, message: "Failed to load session.", type: "danger" });
    }
    setLoading(false);
  }, [sessionId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleStatusChange = (studentId, newStatus) => {
    setEditedParticipants((prev) =>
      prev.map((p) =>
        String(p.studentId) === String(studentId) ? { ...p, status: newStatus } : p
      )
    );
  };

  const handleAddAbsentAsPresent = (student) => {
    setEditedParticipants((prev) => [
      ...prev,
      {
        studentId: student._id,
        name: student.name,
        email: student.email || "",
        status: "present",
        source: "manual",
        activeMinutes: 0,
        attendancePct: 100,
        note: "",
      },
    ]);
    setAbsentStudents((prev) => prev.filter((s) => String(s._id) !== String(student._id)));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Save each changed participant
      for (const p of editedParticipants) {
        await apiClient.put(`/attendance/session/${sessionId}/participant/${p.studentId}`, {
          status: p.status,
          note: p.note,
        });
      }

      // Mark manual attendance for newly added students
      const newManual = editedParticipants.filter((p) => p.source === "manual");
      if (newManual.length > 0) {
        await apiClient.post(`/attendance/manual/${sessionId}`, {
          students: newManual.map((p) => ({
            studentId: p.studentId,
            name: p.name,
            status: p.status,
            note: p.note,
          })),
        });
      }

      setToast({ show: true, message: "Attendance updated successfully.", type: "success" });
      setEditMode(false);
      fetchData();
    } catch {
      setToast({ show: true, message: "Failed to save changes.", type: "danger" });
    }
    setSaving(false);
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "present": return <CheckCircle size={14} className="text-success" />;
      case "partial": return <AlertCircle size={14} className="text-warning" />;
      case "absent": return <XCircle size={14} className="text-danger" />;
      case "dropped": return <XCircle size={14} className="text-muted" />;
      default: return <Clock size={14} className="text-muted" />;
    }
  };

  const filtered = editedParticipants.filter((p) =>
    !searchTerm || p.name?.toLowerCase().includes(searchTerm.toLowerCase())
      || p.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredAbsent = absentStudents.filter((s) =>
    !searchTerm || s.name?.toLowerCase().includes(searchTerm.toLowerCase())
      || s.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="attendance-page sl-page">
        <div className="text-center py-5"><Spinner animation="border" /></div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="attendance-page sl-page">
        <PageHeader backButton onBack={() => navigate(basePath)} title="Session not found" />
        <EmptyState icon={<AlertCircle size={40} />} title="Session not found" />
      </div>
    );
  }

  const presentCount = editedParticipants.filter((p) => p.status === "present").length;
  const partialCount = editedParticipants.filter((p) => p.status === "partial").length;
  const absentCount = editedParticipants.filter((p) => p.status === "absent" || p.status === "dropped").length;
  const durationMins = session.sessionEnd && session.sessionStart
    ? Math.round((new Date(session.sessionEnd) - new Date(session.sessionStart)) / 60000) : 0;

  return (
    <div className="attendance-page sl-page">
      <PageHeader
        backButton
        onBack={() => navigate(basePath)}
        icon={ClipboardCheck}
        title={`Attendance — ${session.batch?.name || "Session"}`}
        description={`${formatDateTime(session.sessionStart)} • ${durationMins} min • ${session.instructor?.name || "Unknown Instructor"}`}
        actions={
          !editMode ? (
            <AppButton variant="outline" size="sm" onClick={() => setEditMode(true)}>
              <Edit3 size={16} /> Edit Attendance
            </AppButton>
          ) : (
            <div className="d-flex gap-2">
              <AppButton variant="ghost" size="sm" onClick={() => { setEditMode(false); fetchData(); }}>
                Cancel
              </AppButton>
              <AppButton variant="primary" size="sm" onClick={handleSave} disabled={saving}>
                <Save size={16} /> {saving ? "Saving…" : "Save Changes"}
              </AppButton>
            </div>
          )
        }
      />

      {/* Stats */}
      <StatsGrid className="attendance-detail-stats">
        <StatsCard title="Total" value={allStudents.length} icon={Users} iconColor="blue" />
        <StatsCard title="Present" value={presentCount} icon={UserCheck} iconColor="emerald" />
        <StatsCard title="Partial" value={partialCount} icon={AlertCircle} iconColor="amber" />
        <StatsCard title="Absent" value={absentCount + absentStudents.length} icon={UserX} iconColor="rose" />
      </StatsGrid>

      {/* Participants */}
      <AppCard className="attendance-detail-card mt-3">
        <AppCardHeader>
          <AppCardTitle>
            <span className="attendance-card-title">
              <Users size={18} /> Participants ({editedParticipants.length})
            </span>
          </AppCardTitle>
          <SearchInput
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search students..."
          />
        </AppCardHeader>
        <AppCardContent>
          <div className="attendance-detail-table-container">
            <table className="attendance-table attendance-table--detail">
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Source</th>
                  <th>Active (min)</th>
                  <th>Attendance %</th>
                  <th>Status</th>
                  {editMode && <th>Note</th>}
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p.studentId} className={`attendance-detail-row--${p.status}`}>
                    <td>
                      <div className="attendance-student-cell">
                        <User size={14} />
                        <div>
                          <div className="attendance-student-cell__name">{p.name}</div>
                          {p.email && <div className="attendance-student-cell__email">{p.email}</div>}
                        </div>
                      </div>
                    </td>
                    <td>
                      {p.source === "online" ? (
                        <span className="attendance-badge attendance-badge--online"><Wifi size={12} /> Online</span>
                      ) : (
                        <span className="attendance-badge attendance-badge--manual"><WifiOff size={12} /> Manual</span>
                      )}
                    </td>
                    <td>{p.activeMinutes || 0}</td>
                    <td>{p.attendancePct || 0}%</td>
                    <td>
                      {editMode ? (
                        <select
                          className="form-select form-select-sm attendance-status-select"
                          value={p.status}
                          onChange={(e) => handleStatusChange(p.studentId, e.target.value)}
                        >
                          <option value="present">Present</option>
                          <option value="partial">Partial</option>
                          <option value="absent">Absent</option>
                          <option value="dropped">Dropped</option>
                        </select>
                      ) : (
                        <span className={`attendance-status-badge attendance-status-badge--${p.status}`}>
                          {getStatusIcon(p.status)} {p.status}
                        </span>
                      )}
                    </td>
                    {editMode && (
                      <td>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Note..."
                          value={p.note}
                          maxLength={200}
                          onChange={(e) =>
                            setEditedParticipants((prev) =>
                              prev.map((x) =>
                                String(x.studentId) === String(p.studentId) ? { ...x, note: e.target.value } : x
                              )
                            )
                          }
                        />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Absent students (not in session) */}
          {(filteredAbsent.length > 0 && (editMode || offlineEnabled)) && (
            <>
              <Separator className="my-3" />
              <h6 className="attendance-absent-heading">
                <UserX size={16} /> Students Not in Session ({filteredAbsent.length})
              </h6>
              <div className="attendance-absent-list">
                {filteredAbsent.map((s) => (
                  <div key={s._id} className="attendance-absent-row">
                    <div className="attendance-absent-row__info">
                      <User size={14} />
                      <span>{s.name}</span>
                      {s.email && <span className="attendance-absent-row__email">{s.email}</span>}
                    </div>
                    {editMode && offlineEnabled && (
                      <AppButton
                        variant="outline"
                        size="sm"
                        onClick={() => handleAddAbsentAsPresent(s)}
                      >
                        <UserCheck size={14} /> Mark Present
                      </AppButton>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </AppCardContent>
      </AppCard>

      {/* Session Meta */}
      {session.manualAttendanceCompleted && (
        <div className="attendance-meta-info mt-3">
          <span>Manual attendance marked by {session.manualMarkedBy?.name || "Admin"} on {formatDateTime(session.manualMarkedAt)}</span>
        </div>
      )}

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

export default AttendanceSessionDetail;
